import { EntityType } from '@/components/entities/entityType';
import {
  ExecutionStepResult,
  PipelineExecutionState,
  StepActionDto,
  StepRunDto,
} from '@/interfaces/pipelineExecution.interface';
import { PipelineInstance } from '@/interfaces/pipelineInstance.interface';
import React, { useEffect, useState } from 'react';
import { SSE } from 'sse.js';
import { useHttpClientContext } from './httpClientContext';
import SseEventSource from './sse';
import { useUserContext } from './userContext';

export interface BiopharmReferenceDataSync {
  status: 'queued';
  tenantId: string;
  pipelineInstanceId?: string;
  storeAgentInstanceId: string;
}

export interface IApiContext {
  apiLoading?: boolean;

  getTenantSettings: () => Promise<Record<string, Record<string, any>>>;
  updateTenantSettings: (settings: Record<string, Record<string, any>>) => Promise<void>;
  getInstanceCounts: () => Promise<{ apps: number; sources: number; agents: number; pipelines: number }>;
  clearCache: () => Promise<void>;
  syncEntityEmbeddings: () => Promise<{ indexed: number; failed: number }>;
  syncBiopharmReferenceData: (tenantId: string) => Promise<BiopharmReferenceDataSync>;
  getAllTenants: () => Promise<any[]>;

  getDomains: () => Promise<any[]>;
  getDomain: (domainId: string) => Promise<any>;
  getTenantDomains: () => Promise<any[]>;
  getDomainMcpTools: (domainId: string) => Promise<any[]>;

  getProgramDefs: (domainId?: string) => Promise<any[]>;
  getProgramDef: (id: string) => Promise<any>;
  saveProgramDef: (program: any) => Promise<any>;
  deleteProgramDef: (id: string) => Promise<void>;

  getDataSourceDefs: (domainId?: string) => Promise<any[]>;
  getDataSourceDef: (dataSourceDefId: string) => Promise<any>;
  getDataSourceInstances: (ownedOnly: boolean, sharedOnly: boolean) => Promise<any[]>;
  getDataSourceInstancesOfType: (dataSourceDefId: string) => Promise<any[]>;
  getDataSourceInstance: (dataSourceDefId: string, dataSourceInstanceId: string) => Promise<any>;
  getNewDataSourceInstance: (dataSourceDefId: string) => Promise<any>;
  createDataSourceInstance: (dataSourceInstance: any) => Promise<any>;
  updateDataSourceInstance: (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    dataSourceInstance: any,
  ) => Promise<any>;
  deleteDataSourceInstance: (dataSourceDefId: string, dataSourceInstanceId: string) => Promise<void>;

  getDataSourceInstanceProcessingStatus: (dataSourceDefId: string, dataSourceInstanceId: string) => Promise<any>;
  getDataSourceInstanceProcessingStatuses: (dataSourceDefId: string, dataSourceInstanceId: string) => Promise<any[]>;
  deleteDataSourceInstanceProcessingStatus: (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    processingStatusId: string,
    pipelineInstanceId?: string,
  ) => Promise<void>;

  getDataSourceInstanceItems: (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;
  getDataSourceInstanceRunItems: (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    processingStatusId: string,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;
  searchDataSourceInstanceItems: (
    dataSourceDefId: string,
    dataSourceId: string,
    documentFilters: any,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;
  getGroupDataSourceItems: (
    dataSourceDefId: string,
    dataSourceId: string,
    group: string,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;

  getDataSourceInstanceItem: (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    itemName: string,
    asPdf: boolean,
    eventId?: string,
    pipelineInstanceId?: string,
  ) => Promise<any>;
  uploadDataSourceInstanceItem: (dataSourceDefId: string, dataSourceInstanceId: string, file: File) => Promise<void>;
  deleteDataSourceInstanceItem: (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    itemName: string,
    pipelineInstanceId?: string,
  ) => Promise<void>;

  getDataSourceItemEventsData: (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    itemId: string,
    onlyDatasource?: boolean,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;

  deleteDataSourceItemEventData: (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    eventDataId: string,
    pipelineInstanceId?: string,
  ) => Promise<void>;

  getDataSourceInstanceUsageStats: (dataSourceDefId: string, dataSourceInstanceId: string) => Promise<any>;

  getAgentDefs: (domainId?: string) => Promise<any[]>;
  getAgentDef: (agentDefId: string) => Promise<any>;
  createAgentDef: (agentDef: any) => Promise<any>;
  updateAgentDef: (agentDefId: string, agentDef: any) => Promise<any>;
  deleteAgentDef: (agentDefId: string) => Promise<void>;

  getNewAgentInstance: (agentDefId: string) => Promise<any>;
  getAgentInstances: (ownedOnly: boolean, sharedOnly: boolean) => Promise<any[]>;
  getAgentInstancesOfType: (agentDefId: string) => Promise<any[]>;
  getAgentInstance: (agentDefId: string, agentInstanceId: string) => Promise<any>;
  createAgentInstance: (agentInstance: any) => Promise<any>;
  updateAgentInstance: (agentDefId: string, agentInstanceId: string, agentInstance: any) => Promise<any>;
  deleteAgentInstance: (agentDefId: string, agentInstanceId: string) => Promise<void>;

  getAgentInstanceItemProcessingStatuses: (
    agentDefId: string,
    agentInstanceId: string,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;
  searchAgentInstanceItemProcessingStatuses: (
    agentDefId: string,
    agentInstanceId: string,
    itemFilters: any,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;
  deleteAgentInstanceItemProcessingStatus: (
    agentDefId: string,
    agentInstanceId: string,
    itemProcessingStatusId: string | number,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;

  getAgentInstanceEventsData: (
    agentDefId: string,
    agentInstanceId: string,
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    dataSourceItemId: string,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;
  deleteAgentInstanceEventData: (
    agentDefId: string,
    agentInstanceId: string,
    eventDataId: string,
    pipelineInstanceId?: string,
  ) => Promise<void>;

  getAgentInstanceUsageStats: (agentDefId: string, agentInstanceId: string) => Promise<any>;
  executeAgentInstance: (
    agentDefId: string,
    agentInstanceId: string,
    options?: {
      pipelineInstanceId?: string;
      dataSourceDefId?: string;
      dataSourceInstanceId?: string;
      dataSourceItemId?: string;
      itemName?: string;
      suppressEvents?: boolean;
    },
  ) => Promise<any>;

  getUserAppDefs: (domainId?: string) => Promise<any[]>;
  getUserAppDef: (agentDefId: string) => Promise<any>;

  getUserAppInstances: (ownedOnly: boolean, sharedOnly: boolean) => Promise<any[]>;
  getUserAppInstancesOfType: (userAppDefId: string) => Promise<any[]>;
  getUserAppInstance: (userAppDefId: string, userAppInstanceId: string) => Promise<any>;
  getNewUserAppInstance: (userAppDefId: string) => Promise<any>;
  createUserAppInstance: (userAppInstance: any) => Promise<any>;
  updateUserAppInstance: (userAppDefId: string, userAppInstanceId: string, userApp: any) => Promise<any>;
  deleteUserAppInstance: (userAppDefId: string, appId: string) => Promise<void>;

  getUserAppInstanceDataSources: (userAppDefId: string, userAppInstanceId: string) => Promise<any[]>;
  getUserAppInstanceSourceEntities: (userAppDefId: string, userAppInstanceId: string) => Promise<any[]>;

  getUserAppInstanceItemProcessingStatuses: (
    userAppDefId: string,
    userAppInstanceId: string,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;
  searchUserAppInstanceItemProcessingStatuses: (
    userAppDefId: string,
    userAppInstanceId: string,
    documentFilters: any,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;
  deleteUserAppInstanceItemProcessingStatus: (
    userAppDefId: string,
    userAppInstanceId: string,
    itemProcessingStatusId: string,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;

  getUserAppInstanceEventsData: (
    userAppDefId: string,
    userAppInstanceId: string,
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    dataSourceItemId: string,
    pipelineInstanceId?: string,
  ) => Promise<any[]>;
  deleteUserAppInstanceEventData: (
    userAppDefId: string,
    agentInstanceId: string,
    eventDataId: string,
    pipelineInstanceId?: string,
  ) => Promise<void>;

  getUserAppInstanceUsageStats: (userAppDefId: string, userAppInstanceId: string) => Promise<any>;

  getUserAppConversations: (userAppDefId: string, userAppInstanceId: string) => Promise<any[]>;
  createNewConversation: (userAppDefId: string, appId: string) => Promise<any>;
  streamAssistantMessage: (
    conversationId: string,
    userMessage: string,
    pipelineInstanceId: string,
    callback: (event: string, payload: any) => void,
    entityContext?: string,
  ) => Promise<any>;
  getAssistantConversations: () => Promise<any[]>;
  deleteAssistantConversation: (conversationId: string) => Promise<void>;
  getAssistantUserInteractions: (conversationId: string) => Promise<any[]>;
  createAssistantInteraction: (
    conversationId: string,
    userMessage: string,
    systemResponse: string,
    toolCalls?: any[],
  ) => Promise<{ conversationId: string; interactionId: string }>;

  streamConversationMessage: (
    userAppDefId: string,
    appId: string,
    conversationId: string,
    userMessage: string,
    selectedModel: string,
    conversationMode: boolean,
    callback: (event: string, payload: any) => void,
  ) => Promise<any>;
  deleteConversation: (userAppDefId: string, appId: string, conversationId: string) => Promise<void>;
  getUserInteractions: (userAppDefId: string, appId: string, conversationId: string) => Promise<any[]>;
  userInteractionFeedback: (
    userAppDefId: string,
    appId: string,
    conversationId: string,
    interactionId: string,
    feedback: boolean,
  ) => Promise<void>;
  deleteUserInteraction: (
    userAppDefId: string,
    appId: string,
    conversationId: string,
    interactionId: string,
  ) => Promise<void>;

  getAllEventDefs: () => Promise<any[]>;
  getEventDefs: (entityType: EntityType, entityDefId: string) => Promise<any[]>;
  getEventSources: (entityType: EntityType, entityDefId: string) => Promise<any[]>;
  getEntitySources: (userAppDefId: string) => Promise<any[]>;
  getPipelineItems: (entityType: EntityType, entityDefId: string, entityId: string) => Promise<any[]>;
  submitPipeline: (entities: any) => Promise<any>;
  getPipelineBlueprints: (domainId?: string) => Promise<any[]>;
  getPipelineInstances: (
    sortBy?: string,
    sortOrder?: 'ASC' | 'DESC',
    includeItems?: boolean,
  ) => Promise<PipelineInstance[]>;
  getPipelineInstance: (id: string) => Promise<PipelineInstance>;
  deletePipelineInstance: (id: string, deleteOrphanedEntities: boolean) => Promise<void>;

  getPipelineExecutionPlan: (pipelineInstanceId: string) => Promise<PipelineExecutionState>;
  executePipelineStep: (pipelineInstanceId: string, stepAction: StepActionDto) => Promise<ExecutionStepResult>;
  resumePipelineStep: (
    pipelineInstanceId: string,
    stepId: string | number,
    resumeDto: {
      approved: boolean;
      feedback?: string;
      modifiedArgs?: any;
      dataSourceItemId?: number | string;
      itemProcessingStatusId?: number | string;
      entityDefId?: string;
    },
  ) => Promise<any>;
  getPendingApprovals: (pipelineInstanceId: string) => Promise<any[]>;
  getPipelineStepRuns: (pipelineInstanceId: string, stepIndex: number) => Promise<StepRunDto[]>;

  getStepRuns: (sessionId: string, stepIndex: number) => Promise<StepRunDto[]>;

  getUsageMetrics: (
    startDate: string,
    endDate?: string,
  ) => Promise<{
    events: { name: string; value: number }[];
    tokens: { name: string; value: number }[];
    latency: { name: string; value: number }[];
  }>;
  getUsageSummary: () => Promise<{ totalEvents: number; totalTokens: number; avgLatencyMs: number }>;

  getUserSurveys: () => Promise<any[]>;
  getUserSurvey: (surveyId: string) => Promise<any>;
  submitUserSurvey: (
    surveyId: string,
    surveyResponses: any,
    callback: (event: string, payload: any) => void,
  ) => Promise<SSE>;
  submitUserRequirements: (surveyId: string, userRequirements: string) => Promise<any>;
  getUserSurveyProcessingResult: (surveyId: string, responseId: string) => Promise<any>;
  createUserSurveyPipeline: (
    surveyId: string,
    responseId: string,
    itemSettingsUpdates?: Array<{ itemIndex: number; settings: Record<string, any> }>,
  ) => Promise<any>;

  getConversationLlms: () => Promise<any[]>;
  getConversationLlmModels: (llm: string) => Promise<any[]>;

  getMcpTools: (namespace?: string, serverUrl?: string, apiKey?: string, domainId?: string) => Promise<any[]>;
  callMcpTool: (
    toolName: string,
    args: Record<string, any>,
    namespace?: string,
    serverUrl?: string,
    apiKey?: string,
    domainId?: string,
  ) => Promise<any>;

  getEmbeddingsLlms: () => Promise<any[]>;
  getEmbeddingsModels: (embeddingApi: string) => Promise<any[]>;

  getDataExtractionLlms: () => Promise<any[]>;
  getDataExtractionLlmModels: (llm: string) => Promise<any[]>;

  getAllLlmModels: () => Promise<any[]>;
  createLlmModel: (model: any) => Promise<any>;
  updateLlmModel: (id: number | string, model: any) => Promise<any>;
  deleteLlmModel: (id: number | string) => Promise<void>;

  getFileTypes: () => Promise<any[]>;
  getFileMimeTypes: () => Promise<any[]>;

  getEntityUsers: (entityType: EntityType, entityDefId: string, entityId: string) => Promise<any[]>;
  getEntityUserInvites: (entityType: EntityType, entityDefId: string, entityId: string) => Promise<any[]>;
  createEntityUser: (
    entityType: EntityType,
    entityDefId: string,
    entityId: string,
    email: string,
    userAccess: any,
  ) => Promise<any>;
  deleteEntityUser: (entityType: EntityType, entityDefId: string, entityId: string, userId: string) => Promise<void>;
  deleteEntityUserInvite: (
    entityType: EntityType,
    entityDefId: string,
    entityId: string,
    requestId: string,
  ) => Promise<void>;

  getEntityLlmUsages: (entityType: EntityType, entityDefId: string, entityId: string) => Promise<any[]>;
  getEntityAuditRecords: (entityType: EntityType, entityDefId: string, entityId: string) => Promise<any[]>;
  getRecentAuditRecords: (limit?: number) => Promise<any[]>;
  getActivityFeed: (limit?: number) => Promise<any[]>;

  runDataSourceIngestionPipeline: (dataSourceDefId: string, dataSourceId: string, reingest: boolean) => Promise<void>;

  getDataSourceItemContentDirectFromSource: (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    filename: string,
    asPdf?: boolean,
  ) => Promise<any>;

  updatePassword: (password: string) => Promise<void>;
  forgotPassword: (forgotPassword: any) => Promise<void>;
  resetPassword: (resetPassword: any) => Promise<void>;

  updateUserProfile: (firstName: string, lastName: string) => Promise<void>;

  closeAccount: () => Promise<void>;

  submitContactUs: (contactUs: any) => Promise<void>;

  searchMarketplace: (query?: string, domains?: string[], tech?: string[]) => Promise<any[]>;

  apiError?: any;
}

const ApiContext = React.createContext<IApiContext>({} as IApiContext);

const ApiContextProvider = ({ children }: any) => {
  const { coreApiHttpClient, authApiHttpClient, llmApiHttpClient, ingestionApiHttpClient } = useHttpClientContext();
  const userContext = useUserContext();
  const sseEventSource = new SseEventSource();

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState();

  useEffect(() => {}, [apiError]);

  const getTenantSettings = async () => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/settings`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deletePipelineInstance = async (id: string, deleteOrphanedEntities: boolean) => {
    setLoading(true);
    try {
      await coreApiHttpClient.delete(
        `/api/tenants/${userContext.user.tenantId}/pipelineInstances/${id}?deleteOrphanedEntities=${deleteOrphanedEntities}`,
      );
      setLoading(false);
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getPipelineInstance = async (id: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/pipelineInstances/${id}`);
      const { data } = response;
      setLoading(false);
      return data && data.data !== undefined ? data.data : data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDomains = async () => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/domains`);
      setLoading(false);
      return response.data?.data || response.data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDomain = async (domainId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/domains/${domainId}`);
      setLoading(false);
      return response.data?.data || response.data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getTenantDomains = async () => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/domains`);
      setLoading(false);
      return response.data?.data || response.data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDomainMcpTools = async (domainId: string) => {
    setLoading(true);
    try {
      const tenantId = userContext?.user?.tenantId;
      const url = tenantId
        ? `/api/tenants/${tenantId}/domains/${domainId}/mcp-tools`
        : `/api/domains/${domainId}/mcp-tools`;
      const response = await coreApiHttpClient.get(url);
      setLoading(false);
      return response.data?.data || response.data || [];
    } catch (error: any) {
      setLoading(false);
      setApiError(error);
      throw error;
    }
  };

  const getProgramDefs = async (domainId?: string) => {
    setLoading(true);
    try {
      const tenantId = userContext?.user?.tenantId || 'genericTenant';
      const response = await coreApiHttpClient.get(`/api/tenants/${tenantId}/programDefs`, {
        params: domainId ? { domainId } : undefined,
      });
      setLoading(false);
      return response.data?.data || response.data || [];
    } catch (error: any) {
      setLoading(false);
      setApiError(error);
      throw error;
    }
  };

  const getProgramDef = async (id: string) => {
    setLoading(true);
    try {
      const tenantId = userContext?.user?.tenantId || 'genericTenant';
      const response = await coreApiHttpClient.get(`/api/tenants/${tenantId}/programDefs/${id}`);
      setLoading(false);
      return response.data?.data || response.data;
    } catch (error: any) {
      setLoading(false);
      setApiError(error);
      throw error;
    }
  };

  const saveProgramDef = async (program: any) => {
    setLoading(true);
    try {
      const tenantId = userContext?.user?.tenantId || 'genericTenant';
      const response = await coreApiHttpClient.post(`/api/tenants/${tenantId}/programDefs`, program);
      setLoading(false);
      return response.data?.data || response.data;
    } catch (error: any) {
      setLoading(false);
      setApiError(error);
      throw error;
    }
  };

  const deleteProgramDef = async (id: string) => {
    setLoading(true);
    try {
      const tenantId = userContext?.user?.tenantId || 'genericTenant';
      await coreApiHttpClient.delete(`/api/tenants/${tenantId}/programDefs/${id}`);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setApiError(error);
      throw error;
    }
  };

  const getPipelineBlueprints = async (domainId?: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/pipelineBlueprints`, {
        params: domainId ? { domainId } : undefined,
      });
      const { data } = response;
      setLoading(false);
      return data && data.data !== undefined ? data.data : data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const updateTenantSettings = async (settings: Record<string, Record<string, any>>) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(`/api/tenants/${userContext.user.tenantId}/settings`, settings);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getInstanceCounts = async () => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/instanceCounts`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const clearCache = async () => {
    setLoading(true);
    try {
      await coreApiHttpClient.post(`/api/tenants/${userContext.user.tenantId}/cache/clear`);
      setLoading(false);
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const syncEntityEmbeddings = async () => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/entities/syncEmbeddings`,
      );
      setLoading(false);
      return response.data?.data || { indexed: 0, failed: 0 };
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  // Queues loading the current BioPharm reference data (Open Targets, DISEASES) for a tenant that has BioPharm enabled
  const syncBiopharmReferenceData = async (tenantId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(`/api/tenants/${tenantId}/biopharm/referenceData/sync`);
      const { data } = response;
      return data && data.data !== undefined ? data.data : data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getDataSourceDefs = async (domainId?: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/dataSourceDefs`, {
        params: domainId ? { domainId } : undefined,
      });
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceDef = async (dataSourceDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/dataSourceDef/${dataSourceDefId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getNewDataSourceInstance = async (dataSourceDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/new`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceInstances = async (ownedOnly: boolean, sharedOnly: boolean) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/dataSourceInstances`, {
        params: { ownedOnly, sharedOnly },
      });
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceInstancesOfType = async (dataSourceDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceInstance = async (dataSourceDefId: string, dataSourceInstanceId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const createDataSourceInstance = async (dataSourceInstance: any): Promise<any> => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/dataSourceInstances`,
        dataSourceInstance,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const updateDataSourceInstance = async (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    dataSourceInstance: any,
  ): Promise<any> => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.put(
        `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}`,
        dataSourceInstance,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteDataSourceInstance = async (dataSourceDefId: string, dataSourceInstanceId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.delete(
        `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceInstanceProcessingStatus = async (dataSourceDefId: string, dataSourceInstanceId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/processingStatus`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceInstanceProcessingStatuses = async (dataSourceDefId: string, dataSourceInstanceId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/processingStatuses`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteDataSourceInstanceProcessingStatus = async (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    processingStatusId: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/processingStatuses/${processingStatusId}`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      await coreApiHttpClient.delete(url);
      setLoading(false);
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceInstanceItems = async (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/items`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.get(url);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceInstanceRunItems = async (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    processingStatusId: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/processingStatuses/${processingStatusId}/items`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.get(url);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const searchDataSourceInstanceItems = async (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    documentFilters: any,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/items/search`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.post(url, documentFilters);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getGroupDataSourceItems = async (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    dataSourceItemGroup: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/items/group/${dataSourceItemGroup}`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.get(url);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceInstanceItem = async (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    dataSourceItemId: string,
    asPdf: boolean,
    eventId?: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/${dataSourceItemId}/content?asPdf=${asPdf}`;
      if (eventId) {
        url += `&eventId=${eventId}`;
      }
      if (pipelineInstanceId) {
        url = `${url}&pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.get(url, { responseType: 'arraybuffer' });
      const { data, headers } = response;
      setLoading(false);
      return { content: data, contentType: headers['content-type'] };
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const uploadDataSourceInstanceItem = async (dataSourceDefId: string, dataSourceInstanceId: string, item: File) => {
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', item);
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/items`,
        form,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteDataSourceInstanceItem = async (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    itemName: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/items/${encodeURIComponent(itemName)}`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.delete(url);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceItemEventsData = async (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    itemId: string,
    onlyDatasource: boolean = false,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/${itemId}/eventsData?t=${Date.now()}`;
      if (pipelineInstanceId) {
        url = `${url}&pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.get(url, { params: { onlyDatasource } });
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteDataSourceItemEventData = async (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    eventDataId: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/eventsData/${eventDataId}`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.delete(url);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceInstanceUsageStats = async (dataSourceDefId: string, dataSourceInstanceId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}/usageStats`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAgentDefs = async (domainId?: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/agentDefs`, {
        params: domainId ? { domainId } : undefined,
      });
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAgentDef = async (agentDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/agentDefs/${agentDefId}`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const createAgentDef = async (agentDef: any) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(`/api/tenants/${userContext.user.tenantId}/agentDefs`, agentDef);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const updateAgentDef = async (agentDefId: string, agentDef: any): Promise<any> => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.put(
        `/api/tenants/${userContext.user.tenantId}/agentDefs/${agentDefId}`,
        agentDef,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteAgentDef = async (agentDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.delete(
        `/api/tenants/${userContext.user.tenantId}/agentDefs/${agentDefId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getNewAgentInstance = async (agentDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}/new`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAgentInstances = async (ownedOnly: boolean, sharedOnly: boolean) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/agentInstances`, {
        params: { ownedOnly, sharedOnly },
      });
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAgentInstancesOfType = async (agentDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAgentInstance = async (agentDefId: string, agentInstanceId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}/${agentInstanceId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const createAgentInstance = async (agentInstances: any) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/agentInstances`,
        agentInstances,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const updateAgentInstance = async (agentDefId: string, agentInstanceId: string, agentInstance: any): Promise<any> => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.put(
        `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}/${agentInstanceId}`,
        agentInstance,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteAgentInstance = async (agentDefId: string, agentInstanceId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.delete(
        `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}/${agentInstanceId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAgentInstanceItemProcessingStatuses = async (
    agentDefId: string,
    agentInstanceId: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}/${agentInstanceId}/itemProcessingStatuses`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.get(url);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const searchAgentInstanceItemProcessingStatuses = async (
    agentDefId: string,
    agentInstanceId: string,
    documentFilters: any,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}/${agentInstanceId}/itemProcessingStatuses/search`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.post(url, documentFilters);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteAgentInstanceItemProcessingStatus = async (
    agentDefId: string,
    agentInstanceId: string,
    itemProcessingStatusId: string | number,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}/${agentInstanceId}/itemProcessingStatuses/${itemProcessingStatusId}`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.delete(url);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAgentInstanceUsageStats = async (agentDefId: string, agentInstanceId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}/${agentInstanceId}/usageStats`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const executeAgentInstance = async (
    agentDefId: string,
    agentInstanceId: string,
    options?: {
      pipelineInstanceId?: string;
      dataSourceDefId?: string;
      dataSourceInstanceId?: string;
      dataSourceItemId?: string;
      itemName?: string;
      suppressEvents?: boolean;
    },
  ) => {
    setLoading(true);
    try {
      const url = `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}/${agentInstanceId}/execute`;
      const response = await coreApiHttpClient.post(url, options || {});
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAgentInstanceEventsData = async (
    agentDefId: string,
    agentInstanceId: string,
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    dataSourceItemId: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}/${agentInstanceId}/${dataSourceDefId}/${dataSourceInstanceId}/${dataSourceItemId}/eventsData?t=${Date.now()}`;
      if (pipelineInstanceId) {
        url = `${url}&pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.get(url);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteAgentInstanceEventData = async (
    agentDefId: string,
    agentInstanceId: string,
    eventDataId: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/agentInstances/${agentDefId}/${agentInstanceId}/eventsData/${eventDataId}`;
      if (pipelineInstanceId) {
        url = `${url}&pipelineInstanceId=${pipelineInstanceId}`;
      }
      await coreApiHttpClient.delete(url);
      setLoading(false);
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserAppDefs = async (domainId?: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/userAppDefs`, {
        params: domainId ? { domainId } : undefined,
      });
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserAppDef = async (userAppId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/userAppDefs/${userAppId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getNewUserAppInstance = async (userAppDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/new`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserAppInstances = async (ownedOnly: boolean, sharedOnly: boolean) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/userAppInstances`, {
        params: { ownedOnly, sharedOnly },
      });
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserAppInstancesOfType = async (userAppDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserAppInstance = async (userAppDefId: string, userAppInstanceId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${userAppInstanceId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserAppInstanceDataSources = async (userAppDefId: string, userAppInstanceId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${userAppInstanceId}/dataSources`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserAppInstanceSourceEntities = async (userAppDefId: string, userAppInstanceId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${userAppInstanceId}/sourceEntities`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserAppInstanceItemProcessingStatuses = async (
    userAppDefId: string,
    userAppInstanceId: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${userAppInstanceId}/itemProcessingStatuses`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.get(url);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const searchUserAppInstanceItemProcessingStatuses = async (
    userAppDefId: string,
    userAppInstanceId: string,
    documentFilters: any,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${userAppInstanceId}/itemProcessingStatuses/search`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.post(url, documentFilters);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteUserAppInstanceItemProcessingStatus = async (
    userAppDefId: string,
    userAppInstanceId: string,
    itemProcessingStatusId: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${userAppInstanceId}/itemProcessingStatuses/${itemProcessingStatusId}`;
      if (pipelineInstanceId) {
        url = `${url}?pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.delete(url);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserAppInstanceEventsData = async (
    userAppDefId: string,
    userAppInstanceId: string,
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    dataSourceItemId: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${userAppInstanceId}/${dataSourceDefId}/${dataSourceInstanceId}/${dataSourceItemId}/eventsData?t=${Date.now()}`;
      if (pipelineInstanceId) {
        url = `${url}&pipelineInstanceId=${pipelineInstanceId}`;
      }
      const response = await coreApiHttpClient.get(url);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteUserAppInstanceEventData = async (
    userAppDefId: string,
    userAppInstanceId: string,
    eventDataId: string,
    pipelineInstanceId?: string,
  ) => {
    setLoading(true);
    try {
      let url = `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${userAppInstanceId}/eventsData/${eventDataId}`;
      if (pipelineInstanceId) {
        url = `${url}&pipelineInstanceId=${pipelineInstanceId}`;
      }
      await coreApiHttpClient.delete(url);
      setLoading(false);
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserAppInstanceUsageStats = async (userAppDefId: string, appId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${appId}/usagetStats`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const createUserAppInstance = async (userAppInstance: any) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances`,
        userAppInstance,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const updateUserAppInstance = async (userAppDefId: string, userAppInstanceId: string, userApp: any) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.put(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${userAppInstanceId}`,
        userApp,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteUserAppInstance = async (userAppDefId: string, appId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.delete(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${appId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserAppConversations = async (userAppDefId: string, appId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${appId}/conversations`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const streamAssistantMessage = async (
    conversationId: string,
    userMessage: string,
    pipelineInstanceId: string,
    callback: (event: string, payload: any) => void,
    entityContext?: string,
  ) => {
    try {
      const payload: any = { content: userMessage };
      if (entityContext) {
        payload.entityContext = entityContext;
      }
      const source = await sseEventSource.getSseEventSource(
        `/api/tenants/${userContext.user.tenantId}/assistant/conversations/${conversationId}/message/stream?pipelineInstanceId=${pipelineInstanceId || ''}`,
        userContext,
        payload,
        callback,
      );
      return source;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAssistantConversations = async (): Promise<any[]> => {
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/assistant/conversations`);
      return response.data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteAssistantConversation = async (conversationId: string): Promise<void> => {
    try {
      await coreApiHttpClient.delete(
        `/api/tenants/${userContext.user.tenantId}/assistant/conversations/${conversationId}`,
      );
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAssistantUserInteractions = async (conversationId: string): Promise<any[]> => {
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/assistant/conversations/${conversationId}/interactions`,
      );
      return response.data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const createAssistantInteraction = async (
    conversationId: string,
    userMessage: string,
    systemResponse: string,
    toolCalls?: any[],
  ): Promise<{ conversationId: string; interactionId: string }> => {
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/assistant/conversations/${conversationId}/interactions`,
        { userMessage, systemResponse, toolCalls },
      );
      return response.data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const streamConversationMessage = async (
    userAppDefId: string,
    appId: string,
    conversationId: string,
    userMessage: string,
    selectedModel: string,
    conversationMode: boolean,
    callback: (event: string, payload: any) => void,
  ) => {
    try {
      const payload = { content: userMessage, selectedModel };
      const source = await sseEventSource.getSseEventSource(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${appId}/conversations/${conversationId}/message/stream?conversationMode=${conversationMode}`,
        userContext,
        payload,
        callback,
      );
      return source;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const createNewConversation = async (userAppDefId: string, appId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${appId}/conversations`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteConversation = async (userAppDefId: string, appId: string, conversationId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.delete(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${appId}/conversations/${conversationId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserInteractions = async (userAppDefId: string, appId: string, conversationId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${appId}/conversations/${conversationId}/userInteractions`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const userInteractionFeedback = async (
    userAppDefId: string,
    appId: string,
    conversationId: string,
    interactionId: string,
    feedback: boolean,
  ) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.put(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${appId}/conversations/${conversationId}/userInteractions/${interactionId}/feedback`,
        {},
        { params: { feedback } },
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteUserInteraction = async (
    userAppDefId: string,
    appId: string,
    conversationId: string,
    interactionId: string,
  ) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.delete(
        `/api/tenants/${userContext.user.tenantId}/userAppInstances/${userAppDefId}/${appId}/conversations/${conversationId}/userInteractions/${interactionId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAllEventDefs = async () => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/eventDefs`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getEventDefs = async (entityType: EntityType, entityDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/eventDefs/${entityType}/${entityDefId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getEventSources = async (entityType: EntityType, entityDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/eventSources/${entityType}/${entityDefId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getEntitySources = async (userAppDefId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/sourceEntites/${userAppDefId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getPipelineItems = async (entityType: EntityType, entityDefId: string, entityId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/pipelineInstances/${entityType}/${entityDefId}/${entityId}`,
        { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache', Expires: '0' } },
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const submitPipeline = async (entities: any): Promise<any> => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/pipelineInstance`,
        entities,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getPipelineInstances = async (sortBy?: string, sortOrder?: 'ASC' | 'DESC', includeItems: boolean = true) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sortBy) params.append('sortBy', sortBy);
      if (sortOrder) params.append('sortOrder', sortOrder);
      if (!includeItems) params.append('includeItems', 'false');
      const queryStr = params.toString() ? `?${params.toString()}` : '';
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/pipelineInstances${queryStr}`,
      );
      const { data } = response;
      setLoading(false);
      return data && data.data !== undefined ? data.data : data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getPipelineExecutionPlan = async (pipelineInstanceId: string): Promise<PipelineExecutionState> => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/pipelineInstances/${pipelineInstanceId}/executionPlan`,
      );
      const { data } = response;
      setLoading(false);
      return data && data.data !== undefined ? data.data : data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const executePipelineStep = async (
    pipelineInstanceId: string,
    stepAction: StepActionDto,
  ): Promise<ExecutionStepResult> => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/pipelineInstances/${pipelineInstanceId}/executeStep`,
        stepAction,
      );
      const { data } = response;
      setLoading(false);
      return data && data.data !== undefined ? data.data : data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getPipelineStepRuns = async (pipelineInstanceId: string, stepIndex: number): Promise<StepRunDto[]> => {
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/pipelineInstances/${pipelineInstanceId}/stepRuns?stepIndex=${stepIndex}`,
      );
      const { data } = response;
      return data && data.data !== undefined ? data.data : data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const resumePipelineStep = async (
    pipelineInstanceId: string,
    stepId: string | number,
    resumeDto: {
      approved: boolean;
      feedback?: string;
      modifiedArgs?: any;
      dataSourceItemId?: number | string;
      itemProcessingStatusId?: number | string;
      entityDefId?: string;
    },
  ): Promise<any> => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/pipelineInstances/${pipelineInstanceId}/steps/${stepId}/resume`,
        resumeDto,
      );
      const { data } = response;
      setLoading(false);
      return data && data.data !== undefined ? data.data : data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getPendingApprovals = async (pipelineInstanceId: string): Promise<any[]> => {
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/pipelineInstances/${pipelineInstanceId}/pendingApprovals`,
      );
      const { data } = response;
      return data && data.data !== undefined ? data.data : data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getStepRuns = async (sessionIdOrPipelineId: string, stepIndex: number): Promise<StepRunDto[]> => {
    return getPipelineStepRuns(sessionIdOrPipelineId, stepIndex);
  };

  const getUsageMetrics = async (startDate: string, endDate?: string) => {
    try {
      const end = endDate || startDate;
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/usage?startDate=${startDate}&endDate=${end}`,
      );
      return response.data || { events: [], tokens: [], latency: [] };
    } catch (error: any) {
      // Return empty arrays on error so UI degrades gracefully
      return { events: [], tokens: [], latency: [] };
    }
  };

  const getUsageSummary = async () => {
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/usage/summary`);
      const { data } = response;
      return data;
    } catch (error: any) {
      return { totalEvents: 0, totalTokens: 0, avgLatencyMs: 0 };
    }
  };

  const getUserSurveys = async () => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/surveys`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserSurvey = async (surveyId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/surveys/${surveyId}`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const submitUserSurvey = async (
    surveyId: string,
    surveyResponse: any,
    callback: (event: string, payload: any) => void,
  ) => {
    try {
      const source = await sseEventSource.getSseEventSource(
        `/api/tenants/${userContext.user.tenantId}/surveys/${surveyId}`,
        userContext,
        surveyResponse,
        callback,
      );
      return source;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const submitUserRequirements = async (surveyId: string, userRequirements: string) => {
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/surveys/${surveyId}/responses/userRequirements`,
        { userRequirements },
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getUserSurveyProcessingResult = async (surveyId: string, responseId: string) => {
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/surveys/${surveyId}/responses/${responseId}/processingResult`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const createUserSurveyPipeline = async (
    surveyId: string,
    responseId: string,
    itemSettingsUpdates?: Array<{ itemIndex: number; settings: Record<string, any> }>,
  ) => {
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/surveys/${surveyId}/responses/${responseId}/pipeline`,
        { itemSettingsUpdates },
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getConversationLlms = async () => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/llms/conversation`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getConversationLlmModels = async (llm: string) => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/llms/${llm}/models/conversation`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getMcpTools = async (namespace?: string, serverUrl?: string, apiKey?: string, domainId?: string) => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.post(`/api/tenants/${userContext.user.tenantId}/mcp/tools`, {
        namespace,
        serverUrl,
        apiKey,
        domainId,
      });
      const { data } = response;
      setLoading(false);
      return data?.tools || [];
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const callMcpTool = async (
    toolName: string,
    args: Record<string, any>,
    namespace?: string,
    serverUrl?: string,
    apiKey?: string,
    domainId?: string,
  ) => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.post(`/api/tenants/${userContext.user.tenantId}/mcp/call-tool`, {
        toolName,
        arguments: args,
        namespace,
        serverUrl,
        apiKey,
        domainId,
      });
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getEmbeddingsLlms = async () => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/llms/embeddings`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getEmbeddingsModels = async (llm: string) => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/llms/${llm}/models/embeddings`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataExtractionLlms = async () => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/llms/documentDataExtraction`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataExtractionLlmModels = async (llm: string) => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/llms/${llm}/models/documentDataExtraction`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAllLlmModels = async () => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/models`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const createLlmModel = async (model: any) => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.post(`/api/tenants/${userContext.user.tenantId}/models`, model);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const updateLlmModel = async (id: number | string, model: any) => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.put(`/api/tenants/${userContext.user.tenantId}/models/${id}`, model);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteLlmModel = async (id: number | string) => {
    setLoading(true);
    try {
      await llmApiHttpClient.delete(`/api/tenants/${userContext.user.tenantId}/models/${id}`);
      setLoading(false);
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getAllTenants = async () => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/all`);
      const { data } = response;
      setLoading(false);
      return data && data.data !== undefined ? data.data : data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getFileTypes = async () => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/fileTypes`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getFileMimeTypes = async () => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(`/api/tenants/${userContext.user.tenantId}/fileMimeTypes`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getEntityUsers = async (entityType: EntityType, entityDefId: string, entityId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/entities/${entityType}/${entityDefId || 'none'}/${entityId}/users`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getEntityUserInvites = async (entityType: EntityType, entityDefId: string, entityId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/entities/${entityType}/${entityDefId || 'none'}/${entityId}/userInvites`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const createEntityUser = async (
    entityType: EntityType,
    entityDefId: string,
    entityId: string,
    email: string,
    userAccess: any,
  ) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/entities/${entityType}/${entityDefId || 'none'}/${entityId}/users`,
        { email, userAccess },
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteEntityUser = async (entityType: EntityType, entityDefId: string, entityId: string, userId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.delete(
        `/api/tenants/${userContext.user.tenantId}/entities/${entityType}/${entityDefId || 'none'}/${entityId}/users/${userId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const deleteEntityUserInvite = async (
    entityType: EntityType,
    entityDefId: string,
    entityId: string,
    requestId: string,
  ) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.delete(
        `/api/tenants/${userContext.user.tenantId}/entities/${entityType}/${entityDefId || 'none'}/${entityId}/userInvites/${requestId}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getEntityLlmUsages = async (entityType: EntityType, entityDefId: string, entityId: string) => {
    setLoading(true);
    try {
      const response = await llmApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/entities/${entityType}/${entityDefId || 'none'}/${entityId}/llmUsage`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getEntityAuditRecords = async (entityType: EntityType, entityDefId: string, entityId: string) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/audit/${entityType}/${entityDefId || 'none'}/${entityId}/auditRecords`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getRecentAuditRecords = async (limit?: number) => {
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/audit/recent${limit ? `?limit=${limit}` : ''}`,
      );
      const { data } = response;
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getActivityFeed = async (limit?: number) => {
    try {
      const response = await coreApiHttpClient.get(
        `/api/tenants/${userContext.user.tenantId}/activityFeed/recent${limit ? `?limit=${limit}` : ''}`,
      );
      const { data } = response;
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const runDataSourceIngestionPipeline = async (dataSourceDefId: string, dataSourceId: string, reingest: boolean) => {
    setLoading(true);
    try {
      const response = await ingestionApiHttpClient.post(
        `/api/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceId}?reingest=${reingest}`,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const getDataSourceItemContentDirectFromSource = async (
    dataSourceDefId: string,
    dataSourceInstanceId: string,
    filename: string,
    asPdf?: boolean,
  ) => {
    setLoading(true);
    try {
      const response = await ingestionApiHttpClient.get(
        `/api/route/tenants/${userContext.user.tenantId}/dataSourceInstances/${dataSourceDefId}/${dataSourceInstanceId}`,
        {
          params: {
            filename,
            asPdf,
          },
          responseType: 'arraybuffer',
        },
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const updatePassword = async (password: string) => {
    setLoading(true);
    try {
      const response = await authApiHttpClient.put(`/api/tenants/${userContext.user.tenantId}/password`, { password });
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const forgotPassword = async (forgotPassword: any) => {
    setLoading(true);
    try {
      const response = await authApiHttpClient.post(`/api/forgotPassword`, forgotPassword);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const resetPassword = async (resetPassword: any) => {
    setLoading(true);
    try {
      const response = await authApiHttpClient.post(`/api/resetPassword`, resetPassword);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const updateUserProfile = async (firstName: string, lastName: string) => {
    setLoading(true);
    try {
      const response = await authApiHttpClient.put(`/api/tenants/${userContext.user.tenantId}/profile`, {
        firstName,
        lastName,
      });
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const closeAccount = async () => {
    setLoading(true);
    try {
      const response = await authApiHttpClient.delete(`/api/tenants/${userContext.user.tenantId}/account`);
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const submitContactUs = async (contactUs: any) => {
    setLoading(true);
    try {
      const response = await coreApiHttpClient.post(
        userContext.user ? `/api/tenants/${userContext.user.tenantId}/contactUs` : `/api/contactUs`,
        contactUs,
      );
      const { data } = response;
      setLoading(false);
      return data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    }
  };

  const searchMarketplace = async (query?: string, domains?: string[], tech?: string[]) => {
    setLoading(true);
    try {
      const params = {} as any;
      if (query) params.query = query;
      if (domains && domains.length > 0) params.domains = domains.join(',');
      if (tech && tech.length > 0) params.tech = tech.join(',');

      const response = await coreApiHttpClient.get(`/api/public/marketplace/search`, { params });
      const { data } = response;
      setLoading(false);
      return data && data.data !== undefined ? data.data : data;
    } catch (error: any) {
      setApiError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const context = {
    apiLoading: loading,

    getTenantSettings,
    updateTenantSettings,
    getInstanceCounts,

    getDataSourceDefs,
    getDataSourceDef,
    getNewDataSourceInstance,
    getDataSourceInstances,
    getDataSourceInstancesOfType,
    getDataSourceInstance,
    createDataSourceInstance,
    updateDataSourceInstance,
    deleteDataSourceInstance,

    getDataSourceInstanceProcessingStatus,
    getDataSourceInstanceProcessingStatuses,
    deleteDataSourceInstanceProcessingStatus,
    getDataSourceInstanceItems,
    getDataSourceInstanceRunItems,
    searchDataSourceInstanceItems,
    getGroupDataSourceItems,
    getDataSourceInstanceItem,
    uploadDataSourceInstanceItem,
    deleteDataSourceInstanceItem,
    getDataSourceItemEventsData,
    deleteDataSourceItemEventData,

    getDataSourceInstanceUsageStats,

    getFileTypes,
    getFileMimeTypes,

    getAgentDefs,
    getAgentDef,
    createAgentDef,
    updateAgentDef,
    deleteAgentDef,

    getNewAgentInstance,
    getAgentInstances,
    getAgentInstancesOfType,
    getAgentInstance,
    createAgentInstance,
    updateAgentInstance,
    deleteAgentInstance,

    getAgentInstanceItemProcessingStatuses,
    searchAgentInstanceItemProcessingStatuses,
    deleteAgentInstanceItemProcessingStatus,

    getAgentInstanceEventsData,
    deleteAgentInstanceEventData,

    getAgentInstanceUsageStats,
    executeAgentInstance,

    getUserAppDef,
    getUserAppDefs,
    getUserAppInstances,
    getUserAppInstancesOfType,
    getUserAppInstance,
    getNewUserAppInstance,
    createUserAppInstance,
    updateUserAppInstance,
    deleteUserAppInstance,

    getUserAppInstanceDataSources,
    getUserAppInstanceSourceEntities,

    getUserAppInstanceItemProcessingStatuses,
    searchUserAppInstanceItemProcessingStatuses,
    deleteUserAppInstanceItemProcessingStatus,

    getUserAppInstanceEventsData,
    deleteUserAppInstanceEventData,

    getUserAppInstanceUsageStats,

    getUserAppConversations,
    streamAssistantMessage,
    getAssistantConversations,
    deleteAssistantConversation,
    getAssistantUserInteractions,
    createAssistantInteraction,
    streamConversationMessage,

    createNewConversation,
    deleteConversation,
    getUserInteractions,
    userInteractionFeedback,
    deleteUserInteraction,

    getAllEventDefs,
    getEventDefs,
    getEventSources,
    getEntitySources,
    getPipelineItems,
    submitPipeline,
    getPipelineBlueprints,
    getPipelineInstances,
    getPipelineInstance,
    deletePipelineInstance,

    getPipelineExecutionPlan,
    executePipelineStep,
    resumePipelineStep,
    getPendingApprovals,
    getPipelineStepRuns,

    getStepRuns,
    getUsageMetrics,
    getUsageSummary,
    getUserSurveys,
    getUserSurvey,
    submitUserSurvey,
    submitUserRequirements,
    getUserSurveyProcessingResult,
    createUserSurveyPipeline,

    getConversationLlms,
    getConversationLlmModels,

    getMcpTools,
    callMcpTool,

    getEmbeddingsLlms,
    getEmbeddingsModels,

    getDataExtractionLlms,
    getDataExtractionLlmModels,

    getAllLlmModels,
    createLlmModel,
    updateLlmModel,
    deleteLlmModel,

    getEntityUsers,
    getEntityUserInvites,
    createEntityUser,
    deleteEntityUser,
    deleteEntityUserInvite,

    getEntityAuditRecords,
    getRecentAuditRecords,
    getActivityFeed,
    getEntityLlmUsages,

    runDataSourceIngestionPipeline,
    getDataSourceItemContentDirectFromSource,

    updatePassword,
    forgotPassword,
    resetPassword,

    updateUserProfile,

    closeAccount,

    submitContactUs,
    searchMarketplace,

    clearCache,
    syncEntityEmbeddings,
    syncBiopharmReferenceData,
    getAllTenants,

    getDomains,
    getDomain,
    getTenantDomains,
    getDomainMcpTools,

    getProgramDefs,
    getProgramDef,
    saveProgramDef,
    deleteProgramDef,

    apiError,
  };

  return <ApiContext.Provider value={context}>{children}</ApiContext.Provider>;
};

function useApiContext() {
  const context = React.useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApiContext must be used within a ApiContextProvider');
  }
  return context;
}

export { ApiContextProvider, useApiContext };
